import {
  DOOR_WIDTH,
  FURNITURE_INFO,
  OFFICE_CELL,
  PERSON_BODY,
  ROOM_KIND_INFO,
  WALL_THICKNESS,
  type DoorSide,
  type OfficeFurniture,
  type OfficeLayout,
  type OfficeRoom,
} from './types';

export interface Point {
  x: number;
  y: number;
}

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A layout compiled for movement: wall segments, solid rectangles and a blocked-cell grid. */
export interface CompiledLayout {
  width: number;
  height: number;
  cell: number;
  cols: number;
  rows: number;
  /** 1 when a person's centre may not be in the cell. */
  blocked: Uint8Array;
  segments: Segment[];
  solids: Rect[];
  /** Rooms sorted smallest first, so a nested room wins in `roomAt`. */
  rooms: OfficeRoom[];
}

export function roomHasWalls(room: OfficeRoom): boolean {
  return room.walls ?? ROOM_KIND_INFO[room.kind].walls;
}

export function roomIsolated(room: OfficeRoom): boolean {
  return ROOM_KIND_INFO[room.kind].isolated && roomHasWalls(room);
}

export function itemSolid(item: OfficeFurniture): boolean {
  return item.solid ?? FURNITURE_INFO[item.kind].solid;
}

/** The axis-aligned footprint of an item, accounting for 90 degree rotations. */
export function itemRect(item: OfficeFurniture): Rect {
  if (item.rotation === 90 || item.rotation === 270) {
    const cx = item.x + item.w / 2;
    const cy = item.y + item.h / 2;
    return { x: cx - item.h / 2, y: cy - item.w / 2, w: item.h, h: item.w };
  }
  return { x: item.x, y: item.y, w: item.w, h: item.h };
}

/** Where the door opening is on a walled room, as the gap's start and end along its side. */
export function doorSpan(room: OfficeRoom): { side: DoorSide; from: number; to: number } | null {
  if (!roomHasWalls(room)) return null;
  const door = room.door ?? { side: 'bottom' as const, offset: Math.round(room.w / 2) };
  const length = door.side === 'top' || door.side === 'bottom' ? room.w : room.h;
  const width = Math.min(DOOR_WIDTH, Math.max(24, length - 32));
  const centre = Math.min(length - 16 - width / 2, Math.max(16 + width / 2, door.offset));
  return { side: door.side, from: centre - width / 2, to: centre + width / 2 };
}

/** The wall segments of a room: four edges, with the door gap cut out of one. */
export function roomSegments(room: OfficeRoom): Segment[] {
  if (room.boundary === 'explicit') return [];
  if (!roomHasWalls(room)) return [];
  const { x, y, w, h } = room;
  const door = doorSpan(room);
  const out: Segment[] = [];
  const edge = (side: 'top' | 'right' | 'bottom' | 'left') => {
    const horizontal = side === 'top' || side === 'bottom';
    const start = horizontal ? x : y;
    const length = horizontal ? w : h;
    const fixed = side === 'top' ? y : side === 'bottom' ? y + h : side === 'left' ? x : x + w;
    const seg = (a: number, b: number): Segment =>
      horizontal ? { x1: a, y1: fixed, x2: b, y2: fixed } : { x1: fixed, y1: a, x2: fixed, y2: b };
    if (door && door.side === side) {
      if (door.from > 0) out.push(seg(start, start + door.from));
      if (door.to < length) out.push(seg(start + door.to, start + length));
    } else {
      out.push(seg(start, start + length));
    }
  };
  edge('top');
  edge('right');
  edge('bottom');
  edge('left');
  return out;
}

function segmentDistanceSquared(px: number, py: number, s: Segment): number {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - s.x1) * dx + (py - s.y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = s.x1 + t * dx;
  const cy = s.y1 + t * dy;
  return (px - cx) ** 2 + (py - cy) ** 2;
}

/** Compiles a layout into a collision grid. Cells are blocked when a person standing there would overlap something. */
export function compileLayout(layout: OfficeLayout): CompiledLayout {
  const cell = OFFICE_CELL;
  const cols = Math.ceil(layout.width / cell);
  const rows = Math.ceil(layout.height / cell);
  const blocked = new Uint8Array(cols * rows);
  const segments: Segment[] = [];
  for (const room of layout.rooms) segments.push(...roomSegments(room));
  for (const wall of layout.walls) segments.push(wall);
  const solids = layout.items.filter(itemSolid).map(itemRect);

  const pad = PERSON_BODY;
  const wallReach = pad + WALL_THICKNESS / 2;
  // Solid rectangles, grown by the body radius.
  for (const r of solids) {
    const c0 = Math.max(0, Math.floor((r.x - pad) / cell));
    const c1 = Math.min(cols - 1, Math.floor((r.x + r.w + pad) / cell));
    const r0 = Math.max(0, Math.floor((r.y - pad) / cell));
    const r1 = Math.min(rows - 1, Math.floor((r.y + r.h + pad) / cell));
    for (let cy = r0; cy <= r1; cy++) for (let cx = c0; cx <= c1; cx++) blocked[cy * cols + cx] = 1;
  }
  // Wall segments: block every cell whose centre is within reach of the segment.
  for (const s of segments) {
    const minX = Math.min(s.x1, s.x2) - wallReach;
    const maxX = Math.max(s.x1, s.x2) + wallReach;
    const minY = Math.min(s.y1, s.y2) - wallReach;
    const maxY = Math.max(s.y1, s.y2) + wallReach;
    const c0 = Math.max(0, Math.floor(minX / cell));
    const c1 = Math.min(cols - 1, Math.floor(maxX / cell));
    const r0 = Math.max(0, Math.floor(minY / cell));
    const r1 = Math.min(rows - 1, Math.floor(maxY / cell));
    const reach2 = wallReach * wallReach;
    for (let cy = r0; cy <= r1; cy++) {
      for (let cx = c0; cx <= c1; cx++) {
        if (segmentDistanceSquared(cx * cell + cell / 2, cy * cell + cell / 2, s) <= reach2)
          blocked[cy * cols + cx] = 1;
      }
    }
  }
  // The world edge.
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const px = cx * cell + cell / 2;
      const py = cy * cell + cell / 2;
      if (px < pad || py < pad || px > layout.width - pad || py > layout.height - pad)
        blocked[cy * cols + cx] = 1;
    }
  }
  const rooms = [...layout.rooms].sort((a, b) => a.w * a.h - b.w * b.h);
  return { width: layout.width, height: layout.height, cell, cols, rows, blocked, segments, solids, rooms };
}

export function cellOf(c: CompiledLayout, x: number, y: number): { cx: number; cy: number } {
  return {
    cx: Math.max(0, Math.min(c.cols - 1, Math.floor(x / c.cell))),
    cy: Math.max(0, Math.min(c.rows - 1, Math.floor(y / c.cell))),
  };
}

export function isBlockedCell(c: CompiledLayout, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx >= c.cols || cy >= c.rows) return true;
  return c.blocked[cy * c.cols + cx] === 1;
}

/** Whether a person may stand with their centre at the point. */
export function isWalkable(c: CompiledLayout, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x > c.width || y > c.height) return false;
  const { cx, cy } = cellOf(c, x, y);
  return !isBlockedCell(c, cx, cy);
}

/** Moves by (dx, dy), sliding along obstacles one axis at a time. */
export function moveWithSliding(c: CompiledLayout, from: Point, dx: number, dy: number): Point {
  let x = from.x;
  let y = from.y;
  if (dx !== 0 && isWalkable(c, x + dx, y)) x += dx;
  if (dy !== 0 && isWalkable(c, x, y + dy)) y += dy;
  return { x, y };
}

export function pointInRect(x: number, y: number, r: Rect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** The room a point is in, smallest room first. */
export function roomAt(c: CompiledLayout, x: number, y: number): OfficeRoom | null {
  for (const room of c.rooms) if (pointInRect(x, y, room)) return room;
  return null;
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Whether two people hear each other: both in the same isolated room, or neither in an isolated
 * room and within the hearing radius. Someone inside walls never hears someone outside them.
 */
export function canHear(c: CompiledLayout, a: Point, b: Point, radius: number): boolean {
  const ra = roomAt(c, a.x, a.y);
  const rb = roomAt(c, b.x, b.y);
  const ia = ra && roomIsolated(ra) ? ra.id : null;
  const ib = rb && roomIsolated(rb) ? rb.id : null;
  if (ia || ib) return ia === ib;
  return distance(a, b) <= radius;
}

/**
 * Loudness of b as heard by a, 0 to 1: full inside a third of the radius, then a smooth fade to
 * silence at the edge. Inside a shared room it is always 1.
 */
export function hearingGain(c: CompiledLayout, a: Point, b: Point, radius: number): number {
  if (!canHear(c, a, b, radius)) return 0;
  const ra = roomAt(c, a.x, a.y);
  if (ra && roomIsolated(ra)) return 1;
  const d = distance(a, b);
  const inner = radius / 3;
  if (d <= inner) return 1;
  const t = Math.min(1, (d - inner) / (radius - inner));
  // smoothstep from 1 down to 0
  const s = t * t * (3 - 2 * t);
  return 1 - s;
}

/** The nearest walkable cell centre to a point, searching outward in rings. */
export function nearestWalkable(c: CompiledLayout, x: number, y: number, maxRings = 40): Point | null {
  const { cx, cy } = cellOf(c, x, y);
  const centre = (col: number, row: number): Point => ({
    x: col * c.cell + c.cell / 2,
    y: row * c.cell + c.cell / 2,
  });
  if (!isBlockedCell(c, cx, cy))
    return {
      x: Math.max(c.cell, Math.min(c.width - c.cell, x)),
      y: Math.max(c.cell, Math.min(c.height - c.cell, y)),
    };
  for (let ring = 1; ring <= maxRings; ring++) {
    let best: Point | null = null;
    let bestD = Infinity;
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
        const col = cx + dx;
        const row = cy + dy;
        if (isBlockedCell(c, col, row)) continue;
        const p = centre(col, row);
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < bestD) {
          bestD = d;
          best = p;
        }
      }
    }
    if (best) return best;
  }
  return null;
}

/** Whether the straight line between two points stays on walkable cells. */
export function lineClear(c: CompiledLayout, a: Point, b: Point): boolean {
  const steps = Math.max(1, Math.ceil(distance(a, b) / (c.cell / 2)));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (!isWalkable(c, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)) return false;
  }
  return true;
}

/** Binary min-heap keyed by f score. */
class Heap {
  private items: { i: number; f: number }[] = [];
  get size() {
    return this.items.length;
  }
  push(i: number, f: number) {
    const items = this.items;
    items.push({ i, f });
    let n = items.length - 1;
    while (n > 0) {
      const p = (n - 1) >> 1;
      if (items[p]!.f <= items[n]!.f) break;
      [items[p], items[n]] = [items[n]!, items[p]!];
      n = p;
    }
  }
  pop(): number {
    const items = this.items;
    const top = items[0]!;
    const last = items.pop()!;
    if (items.length > 0) {
      items[0] = last;
      let n = 0;
      for (;;) {
        const l = n * 2 + 1;
        const r = l + 1;
        let m = n;
        if (l < items.length && items[l]!.f < items[m]!.f) m = l;
        if (r < items.length && items[r]!.f < items[m]!.f) m = r;
        if (m === n) break;
        [items[m], items[n]] = [items[n]!, items[m]!];
        n = m;
      }
    }
    return top.i;
  }
}

/**
 * A* over the collision grid with eight neighbours (no squeezing diagonally between two blocked
 * cells), then pulled straight wherever the line of sight is clear. Returns world points ending at
 * the target, or at the nearest walkable spot when the target itself is blocked. Empty when
 * unreachable.
 */
export function findPath(c: CompiledLayout, from: Point, to: Point, maxExpanded = 40_000): Point[] {
  const goal = nearestWalkable(c, to.x, to.y);
  if (!goal) return [];
  const start = isWalkable(c, from.x, from.y) ? from : nearestWalkable(c, from.x, from.y);
  if (!start) return [];
  if (lineClear(c, start, goal)) return [goal];

  const s = cellOf(c, start.x, start.y);
  const g = cellOf(c, goal.x, goal.y);
  const startIdx = s.cy * c.cols + s.cx;
  const goalIdx = g.cy * c.cols + g.cx;
  const gScore = new Float32Array(c.cols * c.rows).fill(Infinity);
  const parent = new Int32Array(c.cols * c.rows).fill(-1);
  const closed = new Uint8Array(c.cols * c.rows);
  const heap = new Heap();
  const h = (idx: number) => {
    const dx = Math.abs((idx % c.cols) - g.cx);
    const dy = Math.abs(Math.floor(idx / c.cols) - g.cy);
    return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy);
  };
  gScore[startIdx] = 0;
  heap.push(startIdx, h(startIdx));
  let expanded = 0;
  let found = false;
  while (heap.size > 0 && expanded < maxExpanded) {
    const idx = heap.pop();
    if (closed[idx]) continue;
    closed[idx] = 1;
    expanded++;
    if (idx === goalIdx) {
      found = true;
      break;
    }
    const cx = idx % c.cols;
    const cy = Math.floor(idx / c.cols);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = cx + dx;
        const ny = cy + dy;
        if (isBlockedCell(c, nx, ny)) continue;
        if (dx !== 0 && dy !== 0 && (isBlockedCell(c, cx + dx, cy) || isBlockedCell(c, cx, cy + dy)))
          continue;
        const nIdx = ny * c.cols + nx;
        if (closed[nIdx]) continue;
        const cost = gScore[idx]! + (dx !== 0 && dy !== 0 ? Math.SQRT2 : 1);
        if (cost < gScore[nIdx]!) {
          gScore[nIdx] = cost;
          parent[nIdx] = idx;
          heap.push(nIdx, cost + h(nIdx));
        }
      }
    }
  }
  if (!found) return [];
  const cells: Point[] = [];
  for (let idx = goalIdx; idx !== -1 && idx !== startIdx; idx = parent[idx]!) {
    cells.push({
      x: (idx % c.cols) * c.cell + c.cell / 2,
      y: Math.floor(idx / c.cols) * c.cell + c.cell / 2,
    });
  }
  cells.reverse();
  if (cells.length > 0) cells[cells.length - 1] = goal;
  // String pulling: skip every waypoint that can be reached in a straight line from the current one.
  const out: Point[] = [];
  let anchor = start;
  let i = 0;
  while (i < cells.length) {
    let j = cells.length - 1;
    while (j > i && !lineClear(c, anchor, cells[j]!)) j--;
    anchor = cells[j]!;
    out.push(anchor);
    i = j + 1;
  }
  return out;
}

/** Validates a layout beyond its shape: a spawn point you can stand on and rooms inside the world. */
export function layoutProblems(layout: OfficeLayout): string[] {
  const problems: string[] = [];
  const compiled = compileLayout(layout);
  if (!isWalkable(compiled, layout.spawn.x, layout.spawn.y))
    problems.push('The spawn point is inside a wall or furniture');
  for (const room of layout.rooms) {
    if (room.x < 0 || room.y < 0 || room.x + room.w > layout.width || room.y + room.h > layout.height)
      problems.push(`Room "${room.name}" is outside the floor`);
  }
  const ids = new Set<string>();
  for (const thing of [...layout.rooms, ...layout.items, ...layout.walls]) {
    if (ids.has(thing.id)) problems.push(`Duplicate id ${thing.id}`);
    ids.add(thing.id);
  }
  return problems;
}
